import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import cloneDeep from "lodash/cloneDeep";
import set from "lodash/set";
import { toast, Toaster } from "sonner";
import { Loader2, Save, LogOut, Plus, Trash2, RotateCcw, ExternalLink, History, UploadCloud, Eye, Play, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getDraftContent, updateContentApi, publishContentApi, resetContentApi, unreadCount } from "@/lib/api";
import { Input, Area, ImageInput, ArrayEditor, ColorInput, Toggle, Select } from "@/components/admin/fields";
import { Intro } from "@/components/site/Intro";
import { AdminNews } from "@/components/admin/AdminNews";
import { AdminFormations } from "@/components/admin/AdminFormations";
import { AdminFestival } from "@/components/admin/AdminFestival";
import { AdminRealisations } from "@/components/admin/AdminRealisations";
import { AdminActivities } from "@/components/admin/AdminActivities";
import { AdminSubmissions } from "@/components/admin/AdminSubmissions";
import { AdminNewsletter } from "@/components/admin/AdminNewsletter";
import { AdminMaintenance } from "@/components/admin/AdminMaintenance";
import { AdminVideos } from "@/components/admin/AdminVideos";
import { AdminGallery } from "@/components/admin/AdminGallery";
import { AdminEvents } from "@/components/admin/AdminEvents";
import { AdminFestivalArchive } from "@/components/admin/AdminFestivalArchive";
import { AdminFestivalNews } from "@/components/admin/AdminFestivalNews";
import { AdminNav } from "@/components/admin/AdminNav";
import { AdminUsers } from "@/components/admin/AdminUsers";
import { AdminSettings } from "@/components/admin/AdminSettings";
import { AdminSocials } from "@/components/admin/AdminSocials";
import { AdminBackups } from "@/components/admin/AdminBackups";
import { AdminSectionBackgrounds } from "@/components/admin/AdminSectionBackgrounds";
import { AdminPartners } from "@/components/admin/AdminPartners";
import { AdminBlocks } from "@/components/admin/AdminBlocks";
import { AdminTeam } from "@/components/admin/AdminTeam";
import { AdminAnnouncements } from "@/components/admin/AdminAnnouncements";
import { AdminVideoDiagnostics } from "@/components/admin/AdminVideoDiagnostics";
import { FICHE_SAVE_EVENT } from "@/components/admin/Fiche";

const TABS = [
  ["brand", "Identité"], ["nav", "Navigation"], ["hero", "Accueil"], ["mission", "Mission"], ["stats", "Statistiques"],
  ["association", "Association"], ["activities", "Activités"], ["realisations", "Réalisations"],
  ["announcements", "Annonces"],
  ["festival", "Festival"], ["archive", "Archives Festival"], ["festivalnews", "Actualités Festival"], ["testimonials", "Témoignages"], ["team", "Équipe"],
  ["news", "Actualités"], ["formations", "Formations"], ["events", "Agenda"], ["videos", "Galerie vidéo"], ["videodiag", "Diagnostic vidéos"], ["gallery", "Galerie photo"],
  ["partners", "Partenaires"], ["pages", "Pages"],
  ["backgrounds", "Arrière-plans"],
  ["blocks", "Blocs du site"],
  ["maintenance", "Mode maintenance"],
  ["submissions", "Boîte de réception"], ["newsletter", "Newsletter"], ["contact", "Coordonnées"], ["footer", "Pied de page"], ["settings", "Paramètres"], ["backups", "Sauvegardes"], ["users", "Comptes"],
];

// Regroupement des onglets en catégories repliables (aucun onglet supprimé, uniquement organisé)
const CATEGORIES = [
  { id: "contenus", label: "Contenus", keys: ["hero", "mission", "stats", "association", "activities", "realisations", "testimonials", "news", "formations", "events", "announcements"] },
  { id: "communaute", label: "Communauté", keys: ["team", "submissions", "newsletter"] },
  { id: "medias", label: "Médias", keys: ["videos", "videodiag", "gallery"] },
  { id: "festival", label: "Festival", keys: ["festival", "archive", "festivalnews"] },
  { id: "site", label: "Site & Apparence", keys: ["brand", "nav", "partners", "pages", "backgrounds", "blocks", "maintenance", "contact", "footer"] },
  { id: "parametres", label: "Paramètres", keys: ["settings", "backups", "users"] },
];

const slugify = (s = "") => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const BLOCK_TEMPLATES = {
  text: { type: "text", heading: "", body: "" },
  image: { type: "image", image: "", caption: "" },
  video: { type: "video", url: "", title: "" },
};

const PagesEditor = ({ value = [], onChange }) => {
  const pages = value || [];
  const setPage = (i, patch) => { const c = [...pages]; c[i] = { ...c[i], ...patch }; onChange(c); };
  const setBlocks = (i, blocks) => setPage(i, { blocks });
  const addPage = () => onChange([...pages, { title: "Nouvelle page", slug: `page-${pages.length + 1}`, published: false, showInNav: true, blocks: [] }]);
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <span className="text-[11px] tracking-[0.25em] uppercase text-electric">Pages personnalisées</span>
        <button onClick={addPage} data-testid="admin-add-page" className="inline-flex items-center gap-2 text-xs text-white/70 hover:text-electric"><Plus size={14} /> Nouvelle page</button>
      </div>
      {pages.length === 0 && <p className="text-secondary2 text-sm">Aucune page. Créez votre première page (ex : « Galerie », « Espace client »). Publiez-la et cochez « Afficher dans le menu » pour la voir sur le site.</p>}
      {pages.map((pg, i) => (
        <div key={i} data-testid={`admin-page-${i}`} className="rounded-lg border border-white/10 bg-[#0a0a0a] p-4 space-y-4">
          <div className="flex items-center justify-between">
            <a href={`/p/${pg.slug}`} target="_blank" rel="noreferrer" className="text-[10px] text-secondary2 hover:text-electric">/p/{pg.slug || ""}</a>
            <button onClick={() => onChange(pages.filter((_, idx) => idx !== i))} className="text-red-400/70 hover:text-red-400"><Trash2 size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="Titre" value={pg.title} onChange={(v) => setPage(i, { title: v, slug: pg._autoslug !== false ? slugify(v) : pg.slug })} />
            <Input label="Slug (URL)" value={pg.slug} onChange={(v) => setPage(i, { slug: slugify(v), _autoslug: false })} />
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-xs text-secondary2"><input type="checkbox" checked={!!pg.published} onChange={(e) => setPage(i, { published: e.target.checked })} /> Publiée</label>
            <label className="flex items-center gap-2 text-xs text-secondary2"><input type="checkbox" checked={!!pg.showInNav} onChange={(e) => setPage(i, { showInNav: e.target.checked })} /> Afficher dans le menu</label>
          </div>
          <div className="border-t border-white/10 pt-4 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2 mr-2">Blocs de contenu</span>
              {["text", "image", "video"].map((t) => (
                <button key={t} onClick={() => setBlocks(i, [...(pg.blocks || []), { ...BLOCK_TEMPLATES[t] }])} className="text-xs text-white/60 hover:text-electric border border-white/10 rounded px-2 py-1">+ {t === "text" ? "Texte" : t === "image" ? "Image" : "Vidéo"}</button>
              ))}
            </div>
            {(pg.blocks || []).map((bl, bi) => {
              const setBl = (patch) => { const bs = [...pg.blocks]; bs[bi] = { ...bs[bi], ...patch }; setBlocks(i, bs); };
              return (
                <div key={bi} className="rounded border border-white/10 p-3 bg-[#050505] space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-electric">{bl.type}</span>
                    <button onClick={() => setBlocks(i, pg.blocks.filter((_, x) => x !== bi))} className="text-red-400/70 hover:text-red-400"><Trash2 size={13} /></button>
                  </div>
                  {bl.type === "text" && <><Input label="Titre" value={bl.heading} onChange={(v) => setBl({ heading: v })} /><Area label="Texte" value={bl.body} onChange={(v) => setBl({ body: v })} /></>}
                  {bl.type === "image" && <><ImageInput label="Image" value={bl.image} onChange={(v) => setBl({ image: v })} /><Input label="Légende" value={bl.caption} onChange={(v) => setBl({ caption: v })} /></>}
                  {bl.type === "video" && <><Input label="Vidéo (YouTube/Vimeo/.mp4)" value={bl.url} onChange={(v) => setBl({ url: v })} /><Input label="Titre" value={bl.title} onChange={(v) => setBl({ title: v })} /></>}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("hero");
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [unread, setUnread] = useState(0);
  const [introPreview, setIntroPreview] = useState(0);
  const [openCats, setOpenCats] = useState({ contenus: true });

  // Ouvre automatiquement la catégorie contenant l'onglet actif (utile quand un bouton d'en-tête change d'onglet)
  useEffect(() => {
    const cat = CATEGORIES.find((c) => c.keys.includes(tab));
    if (cat) setOpenCats((prev) => (prev[cat.id] ? prev : { ...prev, [cat.id]: true }));
  }, [tab]);

  useEffect(() => { getDraftContent().then((r) => { setData(r.data); setDirty(r.dirty); }); }, []);

  useEffect(() => {
    let alive = true;
    const tick = () => unreadCount().then((r) => { if (alive) setUnread(r.count || 0); }).catch(() => {});
    tick();
    const iv = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, [tab]);

  const upd = (path, val) => { setDirty(true); setData((d) => { const c = cloneDeep(d); set(c, path, val); return c; }); };
  const setArr = (key, arr) => { setDirty(true); setData((d) => ({ ...d, [key]: arr })); };
  const setNestedArr = (parent, key, arr) => { setDirty(true); setData((d) => ({ ...d, [parent]: { ...d[parent], [key]: arr } })); };
  const setSection = (key, val) => { setDirty(true); setData((d) => ({ ...d, [key]: val })); };

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateContentApi(data);
      setDirty(true);
      toast.success("Brouillon enregistré. Cliquez « Publier » pour mettre en ligne.");
      if (res.generatedNews?.length) toast.info(`Actualité créée automatiquement : ${res.generatedNews.join(", ")}`, { duration: 8000 });
    }
    catch { toast.error("Échec de l'enregistrement."); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const upd = await updateContentApi(data);
      const res = await publishContentApi();
      setData(res.data); setDirty(false);
      toast.success("Modifications publiées en ligne !");
      if (upd.generatedNews?.length) toast.info(`Actualité créée automatiquement : ${upd.generatedNews.join(", ")}`, { duration: 8000 });
    } catch { toast.error("Échec de la publication."); }
    finally { setPublishing(false); }
  };

  const preview = () => window.open("/?preview=1", "_blank");

  // Quand une fiche dédiée est ouverte, le bandeau principal est couvert :
  // le bouton « Enregistrer » de la fiche déclenche cet événement → enregistre le brouillon.
  const saveRef = useRef(() => {});
  saveRef.current = save;
  useEffect(() => {
    const h = () => saveRef.current();
    window.addEventListener(FICHE_SAVE_EVENT, h);
    return () => window.removeEventListener(FICHE_SAVE_EVENT, h);
  }, []);

  const reset = async () => {
    if (!window.confirm("Réinitialiser tout le contenu aux valeurs par défaut ? (Cela publie aussi la réinitialisation)")) return;
    const res = await resetContentApi();
    setData(res.data); setDirty(false);
    toast.success("Contenu réinitialisé.");
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  if (!data) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="animate-spin text-electric" /></div>;

  const h = data.hero, m = data.mission, a = data.association, ac = data.activities, f = data.festival, c = data.contact, b = data.brand, tm = data.team, nw = data.news, pt = data.partners;
  const foot = data.footer || {};

  const isOwner = user?.role === "owner";
  const canPublish = user?.role === "owner" || user?.role === "admin";
  const labelOf = (k) => (TABS.find((t) => t[0] === k) || [])[1] || k;
  const isTabVisible = (k) => k !== "users" || isOwner;

  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans">
      <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#181818", border: "1px solid rgba(62,155,255,0.4)", color: "#fff" } }} />
      {introPreview > 0 && <Intro key={introPreview} config={data.intro} force />}
      <header className="sticky top-0 z-30 bg-black/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/karams-logo.png" alt="Karams" className="h-9 w-auto" />
            <nav className="hidden sm:flex items-center gap-1 text-xs">
              <span className="px-3 py-1.5 rounded bg-surface text-electric">Contenu</span>
              <Link to="/admin/analytics" data-testid="admin-goto-analytics" className="px-3 py-1.5 rounded text-secondary2 hover:text-white">Statistiques</Link>
            </nav>
            {dirty && <span data-testid="admin-dirty-badge" className="hidden xl:inline-flex items-center gap-2 text-[10px] tracking-[0.15em] uppercase text-amber-400 border border-amber-400/40 rounded-full px-3 py-1">● Modifications non publiées</span>}
            {dirty && <span data-testid="admin-dirty-dot" className="xl:hidden inline-flex h-2.5 w-2.5 rounded-full bg-amber-400" title="Modifications non publiées" />}
            {user && <span data-testid="admin-current-user" className="hidden xl:inline-flex items-center gap-2 text-[10px] tracking-[0.15em] uppercase text-secondary2 border border-white/10 rounded-full px-3 py-1">{user.name} · {user.role === "owner" ? "Propriétaire" : user.role === "admin" ? "Admin" : "Éditeur"}</span>}
          </div>
          <div className="flex items-center gap-2 lg:gap-3">
            <button onClick={preview} data-testid="admin-preview-btn" className="hidden xl:inline-flex items-center gap-2 text-xs text-secondary2 hover:text-electric"><Eye size={14} /> Aperçu</button>
            <a href="/" target="_blank" rel="noreferrer" data-testid="admin-view-site" className="hidden xl:inline-flex items-center gap-2 text-xs text-secondary2 hover:text-electric"><ExternalLink size={14} /> Site en ligne</a>
            <button onClick={() => setTab("backups")} data-testid="admin-backups-btn" title="Sauvegardes" className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-electric"><History size={14} /> <span className="hidden xl:inline">Sauvegardes</span></button>
            <button onClick={reset} data-testid="admin-reset-btn" title="Réinitialiser" className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-red-400"><RotateCcw size={14} /> <span className="hidden xl:inline">Réinitialiser</span></button>
            <button onClick={save} disabled={saving} data-testid="admin-save-btn" title="Enregistrer le brouillon" className="inline-flex items-center gap-2 px-3 lg:px-4 py-2 rounded-full border border-white/15 text-white text-xs tracking-[0.1em] uppercase font-semibold hover:border-electric hover:text-electric transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} <span className="hidden md:inline">Brouillon</span>
            </button>
            {canPublish && (
              <button onClick={publish} disabled={publishing} data-testid="admin-publish-btn" title="Publier en ligne" className="inline-flex items-center gap-2 px-4 lg:px-5 py-2 rounded-full bg-electric text-black text-xs tracking-[0.1em] uppercase font-semibold hover:bg-white transition-colors disabled:opacity-60">
                {publishing ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} <span className="hidden md:inline">Publier</span>
              </button>
            )}
            <button onClick={doLogout} data-testid="admin-logout-btn" className="text-white/60 hover:text-white"><LogOut size={16} /></button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-8">
        <nav className="flex flex-col gap-1.5 md:sticky md:top-24 md:self-start" data-testid="admin-sidebar-nav">
          {CATEGORIES.map((cat) => {
            const keys = cat.keys.filter(isTabVisible);
            if (keys.length === 0) return null;
            const isOpen = !!openCats[cat.id];
            const catUnread = cat.keys.includes("submissions") ? unread : 0;
            return (
              <div key={cat.id} className="rounded-lg overflow-hidden border border-white/5">
                <button
                  data-testid={`admin-cat-${cat.id}`}
                  onClick={() => setOpenCats((prev) => ({ ...prev, [cat.id]: !prev[cat.id] }))}
                  className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left bg-[#0e0e0e] hover:bg-surface transition-colors"
                >
                  <span className="text-[10px] tracking-[0.22em] uppercase text-secondary2 font-semibold">{cat.label}</span>
                  <span className="flex items-center gap-2">
                    {!isOpen && catUnread > 0 && <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold">{catUnread}</span>}
                    <ChevronDown size={15} className={`text-secondary2 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                  </span>
                </button>
                {isOpen && (
                  <div className="py-1 bg-[#080808]">
                    {keys.map((k) => (
                      <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
                        className={`w-full flex items-center justify-between gap-2 text-left whitespace-nowrap px-4 py-2 text-sm transition-colors ${tab === k ? "bg-surface text-electric" : "text-secondary2 hover:text-white"}`}>
                        <span>{labelOf(k)}</span>
                        {k === "submissions" && unread > 0 && <span data-testid="inbox-unread-badge" className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-semibold">{unread}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="space-y-5 pb-24">
          {tab === "brand" && <>
            <div className="rounded-lg border border-electric/20 bg-[#0a0a0a] p-4 text-sm text-secondary2">Téléversez ici votre logo officiel KARAMS STUDIO (PNG transparent recommandé). Il s'affichera dans l'en-tête et le pied de page du site.</div>
            <ImageInput label="Logo officiel" value={b?.logo} onChange={(v) => upd("brand.logo", v)} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label="Nom de la marque" value={b?.name} onChange={(v) => upd("brand.name", v)} />
              <Input label="Accroche" value={b?.tagline} onChange={(v) => upd("brand.tagline", v)} />
            </div>
            <Input label="Google Analytics 4 — ID de mesure (ex: G-XXXXXXX)" value={b?.ga4Id} onChange={(v) => upd("brand.ga4Id", v)} />
          </>}

          {tab === "hero" && <>
            <div className="rounded-lg border border-electric/20 bg-[#0a0a0a] p-4 space-y-4" data-testid="admin-intro-editor">
              <div className="flex items-center justify-between gap-3">
                <span className="text-[11px] tracking-[0.25em] uppercase text-electric">Intro cinématographique</span>
                <button onClick={() => setIntroPreview((n) => n + 1)} data-testid="admin-intro-preview-btn"
                  className="inline-flex items-center gap-2 text-xs text-white/70 hover:text-electric border border-white/10 rounded-full px-3 py-1.5"><Play size={13} /> Prévisualiser</button>
              </div>
              <p className="text-xs text-secondary2">Écran d'accueil « Bienvenue » affiché une seule fois par visite, puis fondu vers le site.</p>
              <Toggle label="Activer l'intro" checked={(data.intro || {}).enabled !== false} onChange={(v) => upd("intro.enabled", v)} />
              <Input label="Texte de l'intro" value={(data.intro || {}).text} onChange={(v) => upd("intro.text", v)} placeholder="Bienvenue chez Karams Studio" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ColorInput label="Couleur du texte" value={(data.intro || {}).textColor} onChange={(v) => upd("intro.textColor", v)} />
                {(() => {
                  const d = Number((data.intro || {}).durationSec ?? 1.5);
                  const isPreset = [0.5, 1, 1.5, 2, 3].includes(d);
                  return (
                    <Select label="Durée" value={isPreset ? String(d) : "custom"}
                      onChange={(v) => upd("intro.durationSec", v === "custom" ? (isPreset ? 4 : d) : Number(v))}
                      options={[{ value: "0.5", label: "0,5 seconde" }, { value: "1", label: "1 seconde" }, { value: "1.5", label: "1,5 seconde (défaut)" }, { value: "2", label: "2 secondes" }, { value: "3", label: "3 secondes" }, { value: "custom", label: "Personnalisée…" }]} />
                  );
                })()}
              </div>
              {![0.5, 1, 1.5, 2, 3].includes(Number((data.intro || {}).durationSec ?? 1.5)) && (
                <label className="block">
                  <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Durée personnalisée (secondes)</span>
                  <input type="number" min="0.3" step="0.1" value={(data.intro || {}).durationSec ?? 1.5} onChange={(e) => upd("intro.durationSec", Number(e.target.value))}
                    className="mt-2 w-full bg-[#0e0e0e] border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-electric outline-none" />
                </label>
              )}
            </div>

            <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-4 space-y-3">
              <span className="text-[11px] tracking-[0.25em] uppercase text-electric">Titre principal du Hero</span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="Grand titre (ex : KARAMS STUDIO)" value={h.brandTitle} onChange={(v) => upd("hero.brandTitle", v)} placeholder="KARAMS STUDIO" />
                <ColorInput label="Couleur du grand titre" value={h.brandTitleColor} onChange={(v) => upd("hero.brandTitleColor", v)} />
              </div>
            </div>

            <Input label="Sur-titre" value={h.eyebrow} onChange={(v) => upd("hero.eyebrow", v)} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input label="Titre ligne 1" value={h.titleLine1} onChange={(v) => upd("hero.titleLine1", v)} />
              <Input label="Titre ligne 2" value={h.titleLine2} onChange={(v) => upd("hero.titleLine2", v)} />
              <Input label="Titre accent (bleu)" value={h.titleAccent} onChange={(v) => upd("hero.titleAccent", v)} />
            </div>
            <Area label="Sous-titre" value={h.subtitle} onChange={(v) => upd("hero.subtitle", v)} aiContext="le sous-titre d'accroche de la page d'accueil d'une association audiovisuelle" />
            <ImageInput label="Image de fond" value={h.backgroundImage} onChange={(v) => upd("hero.backgroundImage", v)} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label="Vidéo de fond (.mp4, URL)" value={h.backgroundVideoUrl} onChange={(v) => upd("hero.backgroundVideoUrl", v)} />
              <Input label="Showreel (YouTube/Vimeo/.mp4)" value={h.showreelUrl} onChange={(v) => upd("hero.showreelUrl", v)} />
              <Input label="Bouton 1" value={h.ctaPrimary} onChange={(v) => upd("hero.ctaPrimary", v)} />
              <Input label="Bouton 2" value={h.ctaSecondary} onChange={(v) => upd("hero.ctaSecondary", v)} />
            </div>
          </>}

          {tab === "mission" && <>
            <Input label="Sur-titre" value={m.eyebrow} onChange={(v) => upd("mission.eyebrow", v)} />
            <Area label="Titre" value={m.heading} onChange={(v) => upd("mission.heading", v)} />
            <Area label="Texte" value={m.body} onChange={(v) => upd("mission.body", v)} aiContext="le texte de la section mission d'une association audiovisuelle" />
            <ArrayEditor label="Piliers" items={m.pillars} template={{ title: "", text: "" }}
              fields={[{ key: "title", label: "Titre" }, { key: "text", label: "Texte", type: "area" }]}
              onChange={(arr) => setNestedArr("mission", "pillars", arr)} />
          </>}

          {tab === "stats" && <ArrayEditor label="Statistiques" items={data.stats} template={{ value: "", label: "" }}
            fields={[{ key: "value", label: "Valeur (ex: 10+)" }, { key: "label", label: "Libellé" }]}
            onChange={(arr) => setArr("stats", arr)} />}

          {tab === "association" && <>
            <Input label="Sur-titre" value={a.eyebrow} onChange={(v) => upd("association.eyebrow", v)} />
            <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-4 space-y-3">
              <span className="text-[11px] tracking-[0.25em] uppercase text-electric">Bloc photo</span>
              <Toggle label="Afficher le bloc photo" hint="Affiche ou masque toute la grande image de la section Association. Si masqué, le texte occupe toute la largeur." checked={a.imageEnabled !== false} onChange={(v) => upd("association.imageEnabled", v)} />
              <ImageInput label="Image" value={a.image} onChange={(v) => upd("association.image", v)} />
              <Toggle label="Afficher le texte sur la photo" hint="Affiche ou masque le petit texte incrusté en bas de l'image (ex : « Association à but non lucratif »)." checked={a.imageCaptionEnabled !== false} onChange={(v) => upd("association.imageCaptionEnabled", v)} />
              <Input label="Texte sur la photo (laisser vide = aucun texte)" value={a.imageCaption} onChange={(v) => upd("association.imageCaption", v)} placeholder="Association à but non lucratif" />
            </div>
            <Area label="Phrase manifeste" value={a.headline} onChange={(v) => upd("association.headline", v)} />
            <ArrayEditor label="Chapitres" items={a.chapters} template={{ title: "", text: "" }}
              fields={[{ key: "title", label: "Titre" }, { key: "text", label: "Texte", type: "area" }]}
              onChange={(arr) => setNestedArr("association", "chapters", arr)} />
          </>}

          {tab === "activities" && <AdminActivities value={ac || {}} onChange={(v) => setSection("activities", v)} />}

          {tab === "realisations" && <AdminRealisations value={data.realisations || []} meta={data.realisationsMeta || {}}
            onChange={(arr) => setArr("realisations", arr)} onMetaChange={(v) => setSection("realisationsMeta", v)} />}

          {tab === "festival" && <AdminFestival value={f} onChange={(v) => setSection("festival", v)} />}

          {tab === "archive" && <AdminFestivalArchive value={data.festivalArchive || {}} onChange={(v) => setSection("festivalArchive", v)} />}

          {tab === "festivalnews" && <AdminFestivalNews value={(data.festivalArchive || {}).news || []} editions={(data.festivalArchive || {}).editions || []} onChange={(v) => setSection("festivalArchive", { ...(data.festivalArchive || {}), news: v })} />}

          {tab === "testimonials" && <ArrayEditor label="Témoignages" items={data.testimonials} template={{ quote: "", name: "", role: "" }}
            fields={[{ key: "quote", label: "Citation", type: "area" }, { key: "name", label: "Nom" }, { key: "role", label: "Rôle" }]}
            onChange={(arr) => setArr("testimonials", arr)} />}

          {tab === "team" && <AdminTeam value={tm || {}} onChange={(v) => setSection("team", v)} />}

          {tab === "news" && <AdminNews value={nw} content={data} onChange={(v) => setSection("news", v)} />}

          {tab === "formations" && <AdminFormations value={data.formations} onChange={(v) => setSection("formations", v)} />}

          {tab === "events" && <AdminEvents value={data.events || {}} onChange={(v) => setSection("events", v)} />}

          {tab === "videos" && <AdminVideos value={data.videos || {}} onChange={(v) => setSection("videos", v)} />}

          {tab === "gallery" && <AdminGallery value={data.photoGallery || {}} onChange={(v) => setSection("photoGallery", v)} />}

          {tab === "nav" && <AdminNav value={data.nav} pages={data.pages || []} onChange={(v) => setSection("nav", v)} />}

          {tab === "partners" && <AdminPartners value={pt} onChange={(v) => setSection("partners", v)} />}

          {tab === "pages" && <PagesEditor value={data.pages} onChange={(arr) => setArr("pages", arr)} />}

          {tab === "backgrounds" && <AdminSectionBackgrounds value={data.sectionBackgrounds || {}} onChange={(v) => setSection("sectionBackgrounds", v)} />}

          {tab === "blocks" && <AdminBlocks value={data.sectionsVisibility || {}} onChange={(v) => setSection("sectionsVisibility", v)} />}

          {tab === "submissions" && <AdminSubmissions />}
          {tab === "announcements" && <AdminAnnouncements />}
          {tab === "videodiag" && <AdminVideoDiagnostics />}
          {tab === "newsletter" && <AdminNewsletter content={data} />}
          {tab === "maintenance" && <AdminMaintenance />}

          {tab === "contact" && <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="Téléphone" value={c.phone} onChange={(v) => upd("contact.phone", v)} />
            <Input label="Email" value={c.email} onChange={(v) => upd("contact.email", v)} />
            <Input label="Site web (URL)" value={c.website} onChange={(v) => upd("contact.website", v)} />
            <Input label="Instagram (URL)" value={c.instagram} onChange={(v) => upd("contact.instagram", v)} />
            <Input label="YouTube (URL)" value={c.youtube} onChange={(v) => upd("contact.youtube", v)} />
            <AdminSocials items={c.socials || []} onChange={(arr) => setNestedArr("contact", "socials", arr)} />
            <div className="md:col-span-2 mt-2 pt-4 border-t border-white/10">
              <div className="text-[11px] tracking-[0.2em] uppercase text-electric mb-1">Champ « Sujet » (menu déroulant)</div>
              <p className="text-[11px] text-secondary2 mb-3">Ajustez les couleurs si certains éléments sont trop sombres sur le fond noir. Laisser vide = valeurs par défaut.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <ColorInput label="Couleur du texte" value={(c.subjectStyle || {}).textColor} onChange={(v) => upd("contact.subjectStyle.textColor", v)} placeholder="#FFFFFF" />
                <ColorInput label="Couleur de la flèche" value={(c.subjectStyle || {}).arrowColor} onChange={(v) => upd("contact.subjectStyle.arrowColor", v)} placeholder="#2E6BFF" />
                <ColorInput label="Couleur de la ligne" value={(c.subjectStyle || {}).lineColor} onChange={(v) => upd("contact.subjectStyle.lineColor", v)} placeholder="#FFFFFF" />
              </div>
            </div>
          </div>}

          {tab === "users" && isOwner && <AdminUsers currentUser={user} />}

          {tab === "footer" && <>
            <div className="rounded-lg border border-electric/20 bg-[#0a0a0a] p-4 text-sm text-secondary2">Personnalisez le pied de page. Les liens du menu et les réseaux sociaux se gèrent dans les onglets <strong className="text-white">Navigation</strong> et <strong className="text-white">Coordonnées</strong>.</div>
            <ImageInput label="Logo du pied de page (vide = logo original affiché en blanc automatiquement)" value={foot.logo} onChange={(v) => upd("footer.logo", v)} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label="Slogan — début" value={foot.tagline} onChange={(v) => upd("footer.tagline", v)} />
              <Input label="Slogan — accent (en bleu, italique)" value={foot.taglineAccent} onChange={(v) => upd("footer.taglineAccent", v)} />
            </div>
            <Input label="Sous-titre" value={foot.subtitle} onChange={(v) => upd("footer.subtitle", v)} />
            <Input label="Copyright (utilisez {year} pour l'année automatique)" value={foot.copyright} onChange={(v) => upd("footer.copyright", v)} />
          </>}

          {tab === "settings" && <AdminSettings />}

          {tab === "backups" && <AdminBackups onRestored={(d) => { setData(d); setDirty(false); }} />}
        </div>
      </div>
    </div>
  );
}
